
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {Line2} from "../../../libs/three.js/lines/Line2.js";
import {LineGeometry} from "../../../libs/three.js/lines/LineGeometry.js";
import {LineMaterial} from "../../../libs/three.js/lines/LineMaterial.js";
import {DigitizationTools} from "../ToolRegistry.js";
import {createFeature} from "../Feature.js";
import {PipeVisual} from "../PipeVisual.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "../ScreenSpaceScale.js";
import {computePipeResult, RELAXED_REL_RESIDUAL_THRESHOLD, clampDirectionPoint} from "../geometry/pipeAnalysis.js";
import {promptWallThickness} from "../WallThicknessPrompt.js";
import {promptRelaxedFitRetry} from "../RelaxedFitPrompt.js";

import {t, T} from "../i18n/index.js";
// "Rørværktøj": fits a real 3D cylinder to a (possibly only partially
// visible) pipe's point-cloud surface from 2 direction points + 5 seed
// points, then commits it immediately - no review/confirm step (an
// unnecessary extra click; the user can already inspect the committed
// feature and undo/delete it like any other). See geometry/cylinderFit.js,
// geometry/pipeCandidates.js and geometry/pipeAnalysis.js for the actual
// fitting pipeline - this file is purely the capture workflow (direction
// phase -> seed phase -> wall thickness -> compute -> commit) and its
// construction-geometry preview. The tool deactivates itself after every
// completed or cancelled capture (success, rejection, or ESC) - unlike
// other digitization tools, it does not stay armed for a next pipe; the
// user must reselect it to start another measurement.
export const PipeTool = {
	id: "digitize_pipe",
	name: T("Mål rørdimension og koter"),
	category: "specialized",
	// IKKE et feature-capture-værktøj i Registreringer/Målinger-opdelingen:
	// bruges til at bestemme/måle et rørs dimension, centerlinje og koter ud
	// fra punktskyen - hører under MÅLINGER (brugerens eksplicitte
	// beslutning).
	kind: "measurement",
	toolbarGroup: "measurement",
	toolbarOrder: 50,
	icon: "pipe.svg",
	resultGeometryType: "LineString",
	featureType: "Pipe",

	tooltip: T("Bestem et rørs udvendige dimension, centerlinje og koter ud fra den synlige punktsky-overflade. Retningsstykket (de to første punkter) må højst være 0,5 m."),

	instructions: {
		direction1: T("Retning 1/2: Venstreklik på røret for at angive første punkt i rørets retning."),
		direction2: T("Retning 2/2: Venstreklik længere fremme langs røret (højst 0,5 m fra det første punkt) for at angive rørets retning og analyseområde."),
		seed: (n) => t("Overflade {0}/5: Udpeg fem punkter på den synlige røroverflade. Fordel punkterne over så stor en del af krumningen som muligt.", n),
		computing: T("Rørets dimension og centerlinje beregnes ud fra den synlige røroverflade."),
	},

	customCapture(dm, tool){
		runPipeCapture(dm, tool);
	},

	// Restore extension point (see DigitizationModule._buildLoadedFeature): a pipe is not a FeatureVisual (no draggable vertices), so the
	// loader asks the tool for the finished visual. Only the finished result is rebuilt, from the feature's own canonical attributes: the
	// cylinder, centerline, station marker and result label. Returns null (plain line) when the attributes are incomplete.
	createLoadedVisual(item){
		const result = pipeResultFromAttributes(item && item.attributes);
		if(!result) return null;
		const visual = new PipeVisual();
		visual.updateFromResult(result);
		return visual;
	},
};

DigitizationTools.register(PipeTool);

function createMarker(color){
	const geometry = new THREE.SphereGeometry(1, 12, 12);
	const material = new THREE.MeshLambertMaterial({color, depthTest: false, depthWrite: false});
	const mesh = new THREE.Mesh(geometry, material);
	mesh.visible = false;
	return mesh;
}

function createDashedLine(color){
	const geometry = new LineGeometry();
	geometry.setPositions([0, 0, 0, 0, 0, 0]);
	const material = new LineMaterial({color, linewidth: 2, dashSize: 3, gapSize: 2, dashed: true, resolution: new THREE.Vector2(1000, 1000)});
	material.depthTest = false;
	const line = new Line2(geometry, material);
	line.computeLineDistances();
	line.visible = false;
	return line;
}

function setLinePositions(line, a, b){
	line.position.copy(a);
	line.geometry.setPositions([0, 0, 0, ...b.clone().sub(a).toArray()]);
	line.geometry.verticesNeedUpdate = true;
	line.geometry.computeBoundingSphere();
	line.computeLineDistances();
}

const DIRECTION_COLOR = 0x00e5ff;
const SEED_COLOR = 0xffdc00;

// Construction-phase preview: small markers for the 2 direction points and
// up to 5 seed points, connected by dashed guide lines. Pure THREE.Object3D
// bookkeeping, screen-space-scaled every frame like every other
// digitization construction visual.
class PipeCaptureHelper {
	constructor(dm){
		this.dm = dm;
		this.group = new THREE.Group();
		this.directionMarkers = [createMarker(DIRECTION_COLOR), createMarker(DIRECTION_COLOR)];
		this.directionLine = createDashedLine(DIRECTION_COLOR);
		// forhåndsvisning af det andet retningspunkt: enden af stregen fra første punkt til cursoren (klemt til maks. længde)
		this.previewMarker = createMarker(DIRECTION_COLOR);
		this.seedMarkers = Array.from({length: 5}, () => createMarker(SEED_COLOR));

		for(const m of this.directionMarkers) this.group.add(m);
		this.group.add(this.previewMarker);
		this.group.add(this.directionLine);
		for(const m of this.seedMarkers) this.group.add(m);

		dm.scene.add(this.group);

		this._onUpdate = () => this._updateScales();
		dm.viewer.addEventListener("update", this._onUpdate);
	}

	setDirectionPoint(index, position){
		const marker = this.directionMarkers[index];
		marker.position.copy(position);
		marker.visible = true;

		if(this.directionMarkers[0].visible && this.directionMarkers[1].visible){
			setLinePositions(this.directionLine, this.directionMarkers[0].position, this.directionMarkers[1].position);
			this.directionLine.visible = true;
		}
	}

	// Streg fra det første retningspunkt til `position` (null skjuler den igen). Kun mens det andet punkt endnu ikke er sat.
	setDirectionPreview(position){
		if(!position || !this.directionMarkers[0].visible || this.directionMarkers[1].visible){
			this.previewMarker.visible = false;
			if(!this.directionMarkers[1].visible) this.directionLine.visible = false;
			return;
		}
		setLinePositions(this.directionLine, this.directionMarkers[0].position, position);
		this.directionLine.visible = true;
		this.previewMarker.position.copy(position);
		this.previewMarker.visible = true;
	}

	setSeedPoint(index, position){
		const marker = this.seedMarkers[index];
		marker.position.copy(position);
		marker.visible = true;
	}

	_updateScales(){
		const camera = this.dm.viewer.scene.getActiveCamera();
		const size = this.dm.viewer.renderer.getSize(new THREE.Vector2());
		const cameraPos = camera.position;

		for(const m of [...this.directionMarkers, this.previewMarker, ...this.seedMarkers]){
			if(!m.visible) continue;
			const s = computeScreenSpaceScale(camera, m.getWorldPosition(new THREE.Vector3()), cameraPos, size.width, size.height, ScreenSpaceSizes.vertex);
			m.scale.set(s, s, s);
		}

		if(this.directionLine.visible){
			this.directionLine.material.resolution.set(size.width, size.height);
		}
	}

	dispose(){
		this.dm.viewer.removeEventListener("update", this._onUpdate);
		this.dm.scene.remove(this.group);
	}
}

// Captures exactly `count` left-clicked positions, auto-advancing after
// each and finishing (no right-click needed - spec §3) once `count` is
// reached. Resolves the array of THREE.Vector3 positions, or null if the
// capture was cancelled (ESC/tool switch/stop - a Pipe capture has no
// partial-save concept, so *any* cancellation here discards everything the
// same way, unlike Line/Polygon which can keep an already-valid partial
// shape - see runPipeCapture()).
// Exported for reuse by other "N fixed left-clicks, then something happens"
// capture flows (Cirkel/Rektangel/direct Bue - see
// tools/shapeCaptureHelpers.js) - this helper was always generic (only
// depends on dm/tool/count/callbacks, nothing Pipe-specific), just
// previously only ever had one caller.
// options (valgfri):
//   adjustPoint(point, pointsSoFar) -> point: justerer det pickede punkt, før det bruges (fx klemmes til en maks. afstand).
//   rubberBand (standard true): efter hvert accepteret punkt (undtagen det sidste) går den dynamiske streg fra punktet til cursoren
//   (DigitizationModule.setRubberBand). false til flows med egen forhåndsvisning eller hvor en streg ikke giver mening.
//   onHover(point|null, pointsSoFar): kaldes ved musebevægelse med det JUSTEREDE punkt under cursoren (null = intet punkt /
//   fanget slut) - til en live forhåndsvisning. Både hover og klik går gennem samme adjustPoint, så det, brugeren ser, er
//   præcis det, der bliver sat.
export function captureFixedPoints(dm, tool, count, getInstructionText, onEachPoint, options = {}){
	const {adjustPoint = null, onHover = null, rubberBand = true} = options || {};
	return new Promise((resolve) => {
		const domElement = dm.viewer.renderer.domElement;
		const points = [];
		let finished = false;

		const cleanup = () => {
			domElement.removeEventListener("mouseup", onMouseUp, false);
			if(onHover){
				domElement.removeEventListener("mousemove", onMouseMove, false);
				onHover(null, points);
			}
			dm.viewer.removeEventListener("cancel_insertions", onCancel);
			if(rubberBand) dm.clearRubberBand();
		};

		const onCancel = () => {
			if(finished) return;
			finished = true;
			cleanup();
			resolve(null);
		};

		const pickAdjusted = (e) => {
			const location = dm._pickPlacementPosition(e);
			if(!location) return null;
			const picked = location.clone();
			return adjustPoint ? adjustPoint(picked, points) : picked;
		};

		const onMouseMove = (e) => {
			if(finished || dm.isPaused()) return;
			onHover(pickAdjusted(e), points);
		};

		const onMouseUp = (e) => {
			if(finished || dm.isPaused() || dm._consumeCopyClickGuard()) return;
			if(e.button !== THREE.MOUSE.LEFT) return; // right-click is a no-op in this phase, not a finish

			const point = pickAdjusted(e);
			if(!point) return;

			points.push(point);
			dm._markOperationStarted();
			onEachPoint(point, points.length);
			if(rubberBand && points.length < count) dm.setRubberBand(point);

			if(points.length >= count){
				finished = true;
				cleanup();
				resolve(points);
			}else{
				dm._showInstruction(tool.name, getInstructionText(points.length + 1));
			}
		};

		domElement.addEventListener("mouseup", onMouseUp, false);
		if(onHover) domElement.addEventListener("mousemove", onMouseMove, false);
		dm.viewer.addEventListener("cancel_insertions", onCancel);
	});
}

// Waits for a plain left-click anywhere (used for the final "confirm the
// preview" step - no placement/snapping involved, any left-click accepts).
// Resolves true on left-click, false on cancellation.
function waitForReviewClick(dm){
	return new Promise((resolve) => {
		const domElement = dm.viewer.renderer.domElement;
		let finished = false;

		const cleanup = () => {
			domElement.removeEventListener("mouseup", onMouseUp, false);
			dm.viewer.removeEventListener("cancel_insertions", onCancel);
		};

		const onCancel = () => {
			if(finished) return;
			finished = true;
			cleanup();
			resolve(false);
		};

		const onMouseUp = (e) => {
			if(finished || dm.isPaused() || dm._consumeCopyClickGuard()) return;
			if(e.button !== THREE.MOUSE.LEFT) return;

			finished = true;
			cleanup();
			resolve(true);
		};

		domElement.addEventListener("mouseup", onMouseUp, false);
		dm.viewer.addEventListener("cancel_insertions", onCancel);
	});
}

function nextAnimationFrame(){
	return new Promise((resolve) => requestAnimationFrame(resolve));
}

// The finished pipe's canonical attributes: everything a restored pipe needs to show the same result (dimension, wall thickness, levels,
// axis, station) plus the QA figures that stay on the feature. Pure function of the fit result, shared with the restore path below.
export function pipeAttributesFromResult(result){
	return {
		radius: result.radius,
		dimensionMm: result.radius * 2 * 1000,
		wallThicknessMeters: result.wallThicknessMeters,
		innerRadius: result.innerRadius,
		axisPoint: result.axisPoint.toArray(),
		axisDir: result.axisDir.toArray(),
		station: result.station.toArray(),
		centerZ: result.centerZ,
		outerTopZ: result.outerTopZ,
		outerBottomZ: result.outerBottomZ,
		innerTopZ: result.innerTopZ,
		innerBottomZ: result.innerBottomZ,
		sectionLength: result.sectionLength,
		angularCoverageDeg: result.angularCoverageDeg,
		inlierCount: result.inlierCount,
		quality: result.quality,
		relResidual: result.relResidual,
		// true only after an explicit, user-confirmed relaxed-tolerance
		// retry (see RelaxedFitPrompt.js) - persists on the feature so the
		// reduced confidence stays visible after capture, not just in the
		// one-time retry dialog (see PipeVisual.js / FeaturePanel.js).
		reducedAccuracy: result.reducedAccuracy,
		// QA diagnostics (review §48) - not shown in the label, kept for
		// debugging/audit and future quality-threshold tuning
		sigmaSurface: result.sigmaSurface,
		residualToNoiseRatio: result.residualToNoiseRatio,
		radiusSpatialVariation: result.radiusSpatialVariation,
		longitudinalBinsCovered: result.longitudinalBinsCovered,
		longitudinalBinCount: result.longitudinalBinCount,
		seedRegions: result.seedRegions,
		ransacInlierCount: result.ransacInlierCount,
	};
}

// Inverse of pipeAttributesFromResult(): the fit result that PipeVisual.updateFromResult() draws from. null when the attributes do not
// carry a complete result (the loader then falls back to a plain line). Only the finished result is read; no construction state exists
// on a feature (direction/seed points and the fit's intermediate data are gone after completion and are never restored).
export function pipeResultFromAttributes(attributes){
	if(!attributes) return null;
	const vector = (v) => (Array.isArray(v) && v.length === 3 && v.every(Number.isFinite)) ? new THREE.Vector3(v[0], v[1], v[2]) : null;
	const axisDir = vector(attributes.axisDir);
	const station = vector(attributes.station);
	const required = [attributes.radius, attributes.sectionLength, attributes.centerZ, attributes.outerTopZ, attributes.outerBottomZ];
	if(!axisDir || !station || !required.every(Number.isFinite)) return null;
	return {...attributes, axisDir, station, axisPoint: vector(attributes.axisPoint)};
}

function millimetersToMeters(mm){
	return mm / 1000;
}

async function runPipeCapture(dm, tool){
	const viewer = dm.viewer;

	const helper = new PipeCaptureHelper(dm);

	dm._showInstruction(tool.name, tool.instructions.direction1);
	const directionPoints = await captureFixedPoints(
		dm, tool, 2,
		(n) => n === 1 ? tool.instructions.direction1 : tool.instructions.direction2,
		(point, index) => helper.setDirectionPoint(index - 1, point),
		{
			// Efter det første punkt følger en streg cursoren, og den kan ikke blive længere end MAX_PIPE_DIRECTION_LENGTH:
			// ligger cursoren længere væk, stopper stregen (og punktet, et klik sætter) ved grænsen - ingen advarsel nødvendig.
			adjustPoint: (point, soFar) => soFar.length === 1 ? clampDirectionPoint(soFar[0], point) : point,
			onHover: (point) => helper.setDirectionPreview(point),
			rubberBand: false, // retningsstregen tegnes af værktøjet selv (klemt til maks. længde)
		});

	if(!directionPoints){
		helper.dispose();
		// guarded: cancellation can also mean the user switched to a
		// *different* tool, which has already become dm.activeTool - must
		// not deactivate that one
		if(dm.activeTool === tool) dm.stopInsertion();
		return;
	}

	dm._showInstruction(tool.name, tool.instructions.seed(1));
	const seedPoints = await captureFixedPoints(
		dm, tool, 5,
		(n) => tool.instructions.seed(n),
		(point, index) => helper.setSeedPoint(index - 1, point),
		{rubberBand: false}); // overfladepunkterne ligger spredt over røret - en streg mellem dem giver ikke mening

	if(!seedPoints){
		helper.dispose();
		if(dm.activeTool === tool) dm.stopInsertion();
		return;
	}

	// presented right after point registration, immediately before the
	// calculation runs (not at activation) - applies only to the
	// registration just captured, and is never re-prompted for the rest of
	// this cycle. Construction markers stay visible behind the modal popup
	// for context.
	const wallThicknessMm = await promptWallThickness(dm);
	if(wallThicknessMm === undefined){
		helper.dispose();
		if(dm.activeTool === tool) dm.stopInsertion();
		return;
	}
	const wallThicknessMeters = (wallThicknessMm != null && wallThicknessMm > 0) ? millimetersToMeters(wallThicknessMm) : null;

	dm._showInstruction(tool.name, tool.instructions.computing);

	// let the "computing" instruction actually paint before the (synchronous)
	// fit runs, so the user sees it rather than a frozen-looking UI
	await nextAnimationFrame();

	let result = computePipeResult(viewer.scene.pointclouds, directionPoints[0], directionPoints[1], seedPoints, wallThicknessMeters);

	helper.dispose();

	// Explicit, informed relaxed retry (never automatic - see
	// RelaxedFitPrompt.js and pipeAnalysis.js's SAFE_REL_RESIDUAL_THRESHOLD
	// doc comment): only offered for this one specific rejection reason, not
	// e.g. too few points or a seed/surface mismatch, which a looser
	// tolerance wouldn't fix anyway.
	if(result.status !== "ok" && result.code === "fit_quality_too_low"){
		dm._hideToolHint();
		const wantsRetry = await promptRelaxedFitRetry(dm);
		if(wantsRetry){
			result = computePipeResult(viewer.scene.pointclouds, directionPoints[0], directionPoints[1], seedPoints, wallThicknessMeters, {relResidualThreshold: RELAXED_REL_RESIDUAL_THRESHOLD});
		}
	}

	if(result.status !== "ok"){
		dm._showInstruction(tool.name, result.reason);
		await waitForReviewClick(dm); // any click, or ESC, dismisses the message
		dm._hideToolHint();
		if(dm.activeTool === tool) dm.stopInsertion();
		return;
	}

	// --- commit immediately, no review/confirm step - the preview *is* the
	// committed feature's visual, not a separate provisional stage ---
	const preview = new PipeVisual();
	preview.updateFromResult(result);
	dm.scene.add(preview);

	const half = result.axisDir.clone().multiplyScalar(Math.max(result.sectionLength, result.radius * 2.2) / 2);
	const feature = createFeature({
		geometryType: "LineString",
		coordinates: [
			result.station.clone().sub(half).toArray(),
			result.station.clone().add(half).toArray(),
		],
		category: tool.category,
		tool: tool.id,
		featureType: tool.featureType,
		name: `${tool.name} ${dm.featureManager.features.length + 1}`,
		attributes: pipeAttributesFromResult(result),
	});

	preview.setInfo(feature.properties.name, feature.properties.description);

	// Var tre haandkopierede linjer (visuals.set/featureManager.add/
	// registerFeatureSnapTargets) der - modsat alle andre digitization-
	// vaerktoejer - ALDRIG blev konsolideret til _wireFeatureLifecycle()
	// (stabilisation task §11's egen "calculated-geometry tools"-liste
	// naevner ikke Pipe). Konsekvens udover manglende model-stempling
	// (Registreringer-tildelings-diagnoseopgaven, 2026-10): Pipe-features
	// fik aldrig geometry_changed/feature_clicked/onVertexMoved-wiringen fra
	// _wireFeatureLifecycle() - saa fx snap-refresh ved traek af et roer
	// formentlig heller aldrig har virket. Samme rigtige faelles
	// chokepoint som alle andre vaerktoejer nu.
	dm._wireFeatureLifecycle(feature, preview);

	dm.dispatchEvent({type: "feature_created", feature, visual: preview});

	// deactivate after every completed registration - unlike other
	// digitization tools, Pipe does not stay armed for the next capture; the
	// user must reselect the tool to start another measurement
	// (stopInsertion() also hides the instruction overlay)
	dm.stopInsertion();
}
